import React from 'react';
import { View, StyleSheet, Image, ActivityIndicator, StyleProp, ViewStyle } from 'react-native';
import { Text } from '@/components/common/text';
import LinearGradient from 'react-native-linear-gradient';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { GlassCard } from '../GlassCard';
import { StackingRow } from '@/components/shared/redesign/StackingRow';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { WEB_STYLES } from '@/utils/webStyles';
import { hexToRgba } from '@/utils/shared/color';

const CheckIcon = RESIDENTS_SVG_COMPONENTS.CHECK;

interface AutopayCardProps {
  displayName: string;
  initials: string;
  photoUrl?: string;
  propertyName: string;
  autopayEnabled: boolean;
  amount?: string;
  paymentMethodLabel?: string;
  startDate?: string;
  endDate?: string;
  processedDay?: string;
  onSetup?: () => void;
  onEdit?: () => void;
  onRemove?: () => void;
  isSetupDisabled?: boolean;
  /**
   * A removal is in flight: the status reads "Removing autopay…", the details
   * dim and both actions lock until the host's lease refetch lands.
   */
  isRemoving?: boolean;
  /**
   * A setup is in flight: the prompt reads "Setting up autopay…" behind a
   * spinner and the Setup button locks until the host's lease refetch lands.
   */
  isSettingUp?: boolean;
  style?: StyleProp<ViewStyle>;
}

interface InfoRowProps {
  label: string;
  value: string;
  dotColor?: string;
  labelColor: string;
  valueColor: string;
}

const InfoRow: React.FC<InfoRowProps> = React.memo(({
  label,
  value,
  dotColor,
  labelColor,
  valueColor,
}) => (
  <StackingRow style={styles.infoRow} stackedStyle={styles.infoRowStacked}>
    <View style={styles.infoRowLeft}>
      <View style={[styles.infoDot, { backgroundColor: dotColor }]} />
      <Text style={[styles.infoLabel, { color: labelColor }]}>{label}</Text>
    </View>
    <Text style={[styles.infoValue, { color: valueColor }]} numberOfLines={2}>{value}</Text>
  </StackingRow>
));

InfoRow.displayName = 'InfoRow';

export const AutopayCard: React.FC<AutopayCardProps> = React.memo(({
  displayName,
  initials,
  photoUrl,
  propertyName,
  autopayEnabled,
  amount,
  paymentMethodLabel,
  startDate,
  endDate,
  processedDay = '1st of each month',
  onSetup,
  onEdit,
  onRemove,
  isSetupDisabled = false,
  isRemoving = false,
  isSettingUp = false,
  style,
}) => {
  const { colors, semantic } = useThemeColors();
  // The Remove ghost's reds derive from the theme's own red (light and dark
  // differ), never from a literal; hover deepens them.
  const removeRest = { backgroundColor: hexToRgba(colors.accent.red, 0.06), borderColor: hexToRgba(colors.accent.red, 0.12) };
  const removeHover = { backgroundColor: hexToRgba(colors.accent.red, 0.12), borderColor: hexToRgba(colors.accent.red, 0.24) };

  const dotBlue = semantic.interactive.primary;
  const dotGreen = colors.accent.green;
  const dotNeutral = colors.text.dimmer;

  return (
    <GlassCard style={style} borderRadius={16} padding={20}>
      <View style={styles.header}>
        <View style={styles.avatarOuter}>
          {photoUrl ? (
            <Image source={{ uri: photoUrl }} style={styles.avatarImage} />
          ) : (
            <LinearGradient
              colors={['rgba(71,115,254,0.25)', 'rgba(190,94,200,0.25)']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.avatarGradient}
            >
              <Text scale="micro" style={[styles.avatarInitials, { color: colors.text.tertiary }]}>{initials}</Text>
            </LinearGradient>
          )}
        </View>
        <View style={styles.headerText}>
          <Text style={[styles.userName, { color: colors.text.primary }]} numberOfLines={2}>{displayName}</Text>
          <Text style={[styles.propertyName, { color: colors.text.dim }]} numberOfLines={2}>{propertyName}</Text>
        </View>
      </View>

      {autopayEnabled ? (
        <>
          <View style={styles.statusRow}>
            {isRemoving ? (
              <View style={styles.statusSpinner}>
                <ActivityIndicator size="small" color={colors.text.muted} style={styles.statusSpinnerScale} />
              </View>
            ) : (
              <View style={[styles.statusCheck, { backgroundColor: colors.accent.greenBg }]}>
                {CheckIcon && <CheckIcon width={10} height={10} color={colors.accent.green} />}
              </View>
            )}
            <Text
              style={[styles.statusText, { color: isRemoving ? colors.text.muted : colors.accent.green }]}
              accessibilityLiveRegion="polite"
            >
              {isRemoving ? 'Removing autopay…' : 'Autopay active'}
            </Text>
          </View>

          <View style={[styles.divider, { backgroundColor: colors.separator }]} />

          <View style={[styles.detailsContainer, webDimTransition, isRemoving && styles.dimmed]}>
            {amount != null && (
              <InfoRow label="Amount" value={amount} dotColor={dotBlue} labelColor={colors.text.muted} valueColor={colors.text.tertiary} />
            )}
            {paymentMethodLabel && (
              <InfoRow label="Payment method" value={paymentMethodLabel} dotColor={dotGreen} labelColor={colors.text.muted} valueColor={colors.text.tertiary} />
            )}
            {startDate && (
              <InfoRow label="Start date" value={startDate} dotColor={dotNeutral} labelColor={colors.text.muted} valueColor={colors.text.tertiary} />
            )}
            {endDate && (
              <InfoRow label="End date" value={endDate} dotColor={dotNeutral} labelColor={colors.text.muted} valueColor={colors.text.tertiary} />
            )}
            <InfoRow label="Processed" value={processedDay} dotColor={dotNeutral} labelColor={colors.text.muted} valueColor={colors.text.tertiary} />
          </View>

          <View style={styles.actionsRow}>
            {onEdit && (
              <Hoverable
                hover="glass"
                style={[styles.actionButton, { backgroundColor: colors.glass.buttonBg, borderColor: colors.glass.buttonBorder }, webDimTransition, isRemoving && styles.dimmed]}
                onPress={onEdit}
                disabled={isRemoving}
              >
                <Text style={[styles.actionButtonText, { color: semantic.interactive.primary }]}>Edit settings</Text>
              </Hoverable>
            )}
            {onRemove && (
              <Hoverable
                // A red-tinted ghost: it deepens its own red on hover (the
                // neutral row tint would wash the danger colour out).
                hover="none"
                hoverStyle={removeHover}
                style={[styles.removeButton, removeRest, webDimTransition, isRemoving && styles.dimmed]}
                onPress={onRemove}
                disabled={isRemoving}
              >
                <Text style={[styles.removeButtonText, { color: colors.accent.red }]}>Remove</Text>
              </Hoverable>
            )}
          </View>
        </>
      ) : (
        <>
          <View style={[styles.divider, { backgroundColor: colors.separator }]} />
          <View style={styles.setupContainer}>
            {isSettingUp ? (
              <View style={styles.setupStatusRow}>
                <View style={styles.statusSpinner}>
                  <ActivityIndicator size="small" color={colors.text.muted} style={styles.statusSpinnerScale} />
                </View>
                <Text style={[styles.setupDescription, { color: colors.text.muted }]} accessibilityLiveRegion="polite">
                  Setting up autopay…
                </Text>
              </View>
            ) : (
              <Text style={[styles.setupDescription, { color: colors.text.dim }]}>
                Automate your rent payments
              </Text>
            )}
            <Hoverable
              hover="button"
              style={[
                styles.setupButton,
                { backgroundColor: semantic.interactive.primary },
                isSetupDisabled && styles.setupButtonDisabled,
                webDimTransition,
                isSettingUp && styles.dimmed,
              ]}
              onPress={onSetup}
              disabled={isSetupDisabled || isSettingUp}
            >
              <Text style={styles.setupButtonText}>Setup Autopay</Text>
            </Hoverable>
          </View>
        </>
      )}
    </GlassCard>
  );
});

AutopayCard.displayName = 'AutopayCard';

// Eases the dim in on web; a no-op object on native.
const webDimTransition = WEB_STYLES.transition('opacity', '0.2s', 'ease') as any;

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  avatarOuter: {
    width: 48,
    height: 48,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    overflow: 'hidden',
  },
  avatarImage: {
    width: 48,
    height: 48,
  },
  avatarGradient: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarInitials: {
    fontSize: 18,
    fontWeight: '700',
    letterSpacing: -0.3,
  },
  headerText: {
    flex: 1,
    gap: 2,
  },
  userName: {
    ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
  },
  propertyName: {
    ...REDESIGN_TYPOGRAPHY.caption,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 14,
  },
  statusCheck: {
    width: 16,
    height: 16,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  statusText: {
    ...REDESIGN_TYPOGRAPHY.captionMedium,
  },
  statusSpinner: {
    width: 16,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // The small indicator is 20pt; scaled into the 16pt slot the check occupies
  // so the row keeps its height while the state flips.
  statusSpinnerScale: {
    transform: [{ scale: 0.8 }],
  },
  dimmed: {
    opacity: 0.5,
  },
  divider: {
    height: 1,
    marginVertical: 16,
  },
  detailsContainer: {
    backgroundColor: 'rgba(255,255,255,0.02)',
    borderRadius: 12,
    padding: 14,
    gap: 10,
    marginBottom: 16,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  infoRowStacked: {
    gap: 2,
  },
  infoRowLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  infoDot: {
    width: 5,
    height: 5,
    borderRadius: 2.5,
  },
  infoLabel: {
    ...REDESIGN_TYPOGRAPHY.caption,
  },
  infoValue: {
    ...REDESIGN_TYPOGRAPHY.caption,
    fontWeight: '500',
    flexShrink: 1,
    textAlign: 'right',
  },
  actionsRow: {
    flexDirection: 'row',
    gap: 10,
  },
  actionButton: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 10,
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionButtonText: {
    ...REDESIGN_TYPOGRAPHY.captionMedium,
  },
  removeButton: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 10,
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  removeButtonText: {
    ...REDESIGN_TYPOGRAPHY.captionMedium,
  },
  setupContainer: {
    gap: 14,
  },
  setupDescription: {
    ...REDESIGN_TYPOGRAPHY.body,
  },
  // The spinner sits in the same 16pt slot the active state's check uses, and
  // the row takes the prompt's own line height, so the card keeps its height
  // while the state flips.
  setupStatusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  setupButton: {
    borderRadius: REDESIGN_SPACING.buttonRadius,
    minHeight: REDESIGN_SPACING.buttonHeight,
    paddingVertical: 15, // 52 − ~17 line, split (5pt slack)
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  setupButtonDisabled: {
    opacity: 0.4,
  },
  setupButtonText: {
    ...REDESIGN_TYPOGRAPHY.button,
    color: '#FFFFFF',
  },
});
